// Site-wide small callouts, section headers, and highlighted text blocks that
// patients or physicians might search for. Each entry links back to the page
// that contains the content, so search takes users directly there.
//
// Keep this file curated (not auto-generated) so we can control what surfaces
// in the search dropdown and phrase things in patient-friendly language.

export const SITE_HIGHLIGHTS = [
    // ------------------------------------------------------------------
    // Virtual Consultations page
    // ------------------------------------------------------------------
    {
        title: "Setting up your appointment",
        body: "Once your referral is accepted, our office schedules through the Ocean Platform and sends a confirmation email with the details.",
        to: "/virtual-consultations",
        group: "Virtual Care",
        keywords: "appointment booking scheduling confirmation email ocean platform",
    },
    {
        title: "You'll receive a confirmation email",
        body: "Ocean Platform sends a confirmation email with your date, time, and provider once your referral is accepted.",
        to: "/virtual-consultations",
        group: "Virtual Care",
        keywords: "confirmation email ocean oceanmd date time provider",
    },
    {
        title: "What this email means",
        body: "Your virtual consultation is officially booked. Watch for the Insig Health text-message link on the day of your visit.",
        to: "/virtual-consultations",
        group: "Virtual Care",
        keywords: "email booked insig text link",
    },
    {
        title: "Procedures booked the same way",
        body: "Procedure appointments at Queensway Carleton Hospital also come with an Ocean Platform confirmation email and reminders.",
        to: "/virtual-consultations",
        group: "Virtual Care",
        keywords: "procedure booking ocean queensway reminder",
    },
    {
        title: "Please check your junk / spam folder",
        body: "Emails from Dr. Kichian's office sometimes land in junk mail — especially the first one. Check there if you don't see it.",
        to: "/virtual-consultations",
        group: "Virtual Care",
        keywords: "junk spam email missing inbox ocean folder",
    },
    {
        title: "We use Insig Health",
        body: "A secure link is sent as a text message to your cellphone about 15 minutes before your appointment. No app to install.",
        to: "/virtual-consultations",
        group: "Virtual Care",
        keywords: "insig health telemedicine platform video link sms text message 15 minutes",
    },
    {
        title: "The virtual visit platform",
        body: "Dr. Kichian uses the Insig Health telemedicine app for virtual consultations.",
        to: "/virtual-consultations",
        group: "Virtual Care",
        keywords: "platform insig telemedicine video visit",
    },
    {
        title: "In-person visits are available on request",
        body: "Patients can request an in-person appointment on demand at Queensway Carleton Hospital — expect a longer wait than virtual.",
        to: "/virtual-consultations",
        group: "Virtual Care",
        keywords: "in-person office visit queensway wait time",
    },
    {
        title: "See a specialist from anywhere",
        body: "No travel, no waiting rooms. Consultations happen from the comfort of your home.",
        to: "/virtual-consultations",
        group: "Virtual Care",
        keywords: "specialist anywhere home virtual",
    },
    {
        title: "Confidential & professional",
        body: "All communications are handled with strict privacy standards (PIPEDA / PHIPA). Your information stays confidential.",
        to: "/virtual-consultations",
        group: "Virtual Care",
        keywords: "confidential privacy pipeda phipa secure",
    },
    {
        title: "Privacy & confidentiality",
        body: "Referral and clinical information is sent through secure physician-to-physician channels.",
        to: "/virtual-consultations",
        group: "Virtual Care",
        keywords: "privacy confidentiality secure phi",
    },

    // ------------------------------------------------------------------
    // Referring Physicians page
    // ------------------------------------------------------------------
    {
        title: "Referrals accepted from family physicians only",
        body: "Dr. Kichian does not accept self-referrals from patients. Please ask your family physician to fax a referral.",
        to: "/referrals",
        group: "Referring Physicians",
        keywords: "family physician self referral fax letterhead",
    },
    {
        title: "The patient's email address is crucial for scheduling",
        body: "Please include the patient's current email address on the referral — the office uses Ocean Platform to send appointment confirmations.",
        to: "/referrals",
        group: "Referring Physicians",
        keywords: "email patient scheduling ocean platform referral",
    },
    {
        title: "Fax the referral to our office",
        body: "Family physicians should fax completed referrals to (613) 728-0091 on office letterhead. Fax is our preferred and secure channel.",
        to: "/referrals",
        group: "Referring Physicians",
        keywords: "fax referral 613 728 0091 letterhead",
    },
    {
        title: "Typical wait times",
        body: "Routine consultation: 4–8 weeks. Urgent: within 1 week. Emergent: call Queensway Carleton Hospital and page Dr. Kichian.",
        to: "/referrals",
        group: "Referring Physicians",
        keywords: "wait times routine urgent emergent queensway page",
    },
    {
        title: "How to send a referral",
        body: "Fax completed referrals to (613) 728-0091 on clinic letterhead. Include indication, relevant history, and recent investigations.",
        to: "/referrals",
        group: "Referring Physicians",
        keywords: "how send referral fax letterhead",
    },

    // ------------------------------------------------------------------
    // Bowel Prep pages
    // ------------------------------------------------------------------
    {
        title: "Which prep is for me?",
        body: "Most patients take Pico-Salax. Patients with kidney disease, congestive heart failure, or liver disease take Peglite.",
        to: "/resources/bowel-prep-guide",
        group: "Bowel prep",
        keywords: "which prep pico peglite kidney heart liver",
    },
    {
        title: "Where to get your prep kit",
        body: "Both Pico-Salax and Peglite kits are available over the counter at any pharmacy — no prescription needed.",
        to: "/resources/bowel-prep-guide",
        group: "Bowel prep",
        keywords: "buy prep pharmacy over the counter otc prescription",
    },
    {
        title: "Buy Pico-Salax over the counter",
        body: "No prescription needed. If you prefer a written prescription for insurance, call our office.",
        to: "/resources/bowel-prep-guide/pico-salax",
        group: "Bowel prep",
        keywords: "pico-salax purg-odan pharmacy otc prescription",
    },
    {
        title: "Buy Peglite over the counter",
        body: "No prescription needed. If you prefer a written prescription for insurance, call our office.",
        to: "/resources/bowel-prep-guide/peglite",
        group: "Bowel prep",
        keywords: "peglite colyte pharmacy otc prescription kidney heart",
    },
    {
        title: "Important safety note (Pico-Salax)",
        body: "Pico-Salax is NOT safe for patients with kidney disease, heart failure, liver disease with ascites, pregnancy, or after colectomy.",
        to: "/resources/bowel-prep-guide/pico-salax",
        group: "Bowel prep",
        keywords: "pico contraindication safety kidney heart liver pregnancy colectomy",
    },
    {
        title: "Call us if you experience",
        body: "Uncontrolled vomiting, severe abdominal pain, or signs of dehydration during prep — call our office.",
        to: "/resources/bowel-prep-guide",
        group: "Bowel prep",
        keywords: "red flag call vomiting pain dehydration",
    },

    // ------------------------------------------------------------------
    // Home / general
    // ------------------------------------------------------------------
    {
        title: "Royal College fellow",
        body: "Dr. Kichian is a Fellow of the Royal College of Physicians and Surgeons of Canada (FRCPC).",
        to: "/about",
        group: "About",
        keywords: "royal college fellow frcpc credentials board certified",
    },
    {
        title: "Modern diagnostics",
        body: "High-definition endoscopy and collaboration with Queensway Carleton diagnostic imaging.",
        to: "/technology",
        group: "Technology",
        keywords: "modern diagnostics hd endoscopy imaging",
    },
    {
        title: "Patient-first approach",
        body: "Warm, plain-language, patient-centered care rooted in evidence-based medicine.",
        to: "/about",
        group: "About",
        keywords: "patient-first approach evidence based warm",
    },
    {
        title: "Queensway Carleton Hospital location",
        body: "3045 Baseline Road, Ottawa, ON K2H 8P4. All procedures are performed here.",
        to: "/about",
        group: "About",
        keywords: "location address queensway carleton hospital ottawa baseline",
    },
];
